import type { ClientComment } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { CommentItem } from '@/features/posts/components/comments/CommentItem';
import { CommentsHeader } from '@/features/posts/components/comments/CommentsHeader';
import { useCommentSortStore } from '@/features/posts/store/commentSortStore';
import { CommentSortEnum } from '@/features/posts/types/commentSort';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { EmptyCard } from '@/shared/components/EmptyCard';
import { StateView } from '@/shared/components/state-view/StateView';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    comments: {
      flexGrow: 1,
      padding: 16,
      backgroundColor: theme.colors.surfaceContainerLow,
      gap: 20
    }
  });

type Props = {
  commentsAllowed: boolean;
  isLoading?: boolean;
  isError?: boolean;
  comments?: ClientComment[];
  onEdit: Dispatch<SetStateAction<ClientComment | undefined>>;
  onReply: Dispatch<SetStateAction<ClientComment | undefined>>;
};

export const Comments: FC<Props> = ({
  commentsAllowed,
  isLoading,
  isError,
  comments,
  onEdit,
  onReply
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-detail'
  });

  const sort = useCommentSortStore((state) => state.sort);

  const sortedComments = useMemo(
    () =>
      sort === CommentSortEnum.OLDEST
        ? comments
        : [...(comments ?? [])].reverse(),
    [comments, sort]
  );

  return (
    <StateView
      asView
      viewStyle={styles.comments}
      isLoading={isLoading}
      isError={isError}
      isEmpty={!hasItems(sortedComments)}
      state={{
        // Todo: add error state or at least provide the labels
        empty: (
          <EmptyCard
            mode="contained"
            backgroundColor={theme.colors.surfaceContainerLow}
            description={t('state.empty.subtitle')}
          />
        )
      }}
    >
      <CommentsHeader count={sortedComments?.length} />
      {sortedComments?.map((comment) => (
        <CommentItem
          key={comment.id}
          commentsAllowed={commentsAllowed}
          comment={comment}
          onEdit={onEdit}
          onReply={onReply}
        />
      ))}
    </StateView>
  );
};
